import type { DigitalTwinModelsAddResponse } from '@azure/digital-twins-core'
import { computed, ref } from 'vue'
import { defineStore } from 'pinia'

import type { DigitalTwinsModelData } from 'models/DigitalTwinsModelData'
import type { DtdlInterface } from 'models/DtdlInterface'
import type { DtdlProperty } from 'models/DtdlProperty'
import type { DtdlRelationship } from 'models/DtdlRelationship'
import type { DtdlTelemetry } from 'models/DtdlTelemetry'
import type { DtdlComponent } from 'models/DtdlComponent'
import type { DtdlMapSchema, DtdlObjectSchema } from 'models/Dtdl'
import type { BasicDigitalTwin } from 'models/BasicDigitalTwin'
import type { ViewerDataTwin } from 'models/ViewerDataTwin'
import type { BlobTwin, AutodeskFileTwin, OneLakeFileTwin } from 'models/FileTwin'
import type { EventTwin } from 'models/EventTwin'
import type { DigitalTwinsModelDataExtended } from 'models/DigitalTwinsModelDataExtended'
import { twinsApiService } from 'services/TwinsApiService'
import { envApiService } from 'services/AdminApiService'
import { getDescription, getDisplayName, getFormattedValueByProperty } from 'utils/dtdl'
import { getCommentValue } from 'utils/comments'
import { isNumeric } from 'utils/type-checks'
import { jexlEval } from 'utils/jexl'
import {
  ALLOWED_BLOB_MODEL_IDS,
  ALLOWED_VIEWER_DATA_MODEL_IDS,
  ALLOWED_EVENT_MODEL_IDS,
  AUTODESK_FILE_MODEL_ID,
  ONELAKE_FILE_MODEL_ID
} from 'services/constants'

export interface IModelApiService {
  getModels: () => Promise<DigitalTwinsModelData[]>
  addModels?: (models: DtdlInterface[]) => Promise<DigitalTwinModelsAddResponse | undefined>
  deleteModel?: (modelId: string) => Promise<unknown>
  decommissionModel?: (modelId: string) => Promise<unknown>
}

/**
 * Model data for the models that were just created by {@link IModelApiService.addModels}.
 *
 * Prefers the definitions echoed back by the service, but falls back to the submitted DTDL
 * for anything the response leaves out. Not every service returns the full model data on a
 * create (and a create recovered from a non-201 success response has no body at all), and
 * silently dropping those entries is what made a freshly added model stay invisible in the
 * model tree until a manual refresh (AB#4313).
 */
const createdModelData = (
  response: DigitalTwinModelsAddResponse | undefined,
  submitted: DtdlInterface[]
): DigitalTwinsModelData[] => {
  const created = new Map<string, DigitalTwinsModelData>()
  for (const entry of response ?? []) {
    if (entry?.id && entry?.model) created.set(entry.id, entry as DigitalTwinsModelData)
  }
  for (const model of submitted) {
    const id = model['@id']
    if (id && !created.has(id)) created.set(id, { id, model })
  }
  return [...created.values()]
}

export const createModelStore = (storeId: string, service: IModelApiService) => {
  /**
   * In-flight {@link getModels} promise. Kept in the closure rather than in store
   * state because a promise isn't serialisable. Concurrent callers share it, so the
   * burst of `ensureModelsLoaded` calls a dashboard fires on load (one per feature
   * and per nested-feature tile) results in a single request.
   */
  let modelsLoad: Promise<void> | undefined

  /**
   * Generation of the most recently *started* {@link getModels} request. A load only
   * publishes its result when it is still the newest one, so a request issued before a
   * mutation can never overwrite the forced refresh that was issued after it.
   */
  let modelsLoadGeneration = 0

  return defineStore(storeId, () => {
    const models = ref<DigitalTwinsModelDataExtended[]>([])

    /** Get all properties of the model */
    function properties(id: string): DtdlProperty[] {
      return models.value.find((m) => m.id === id)?.properties || []
    }

    /** Get all components of the model */
    function components(id: string): DtdlComponent[] {
      return models.value.find((m) => m.id === id)?.components || []
    }

    /** Get all relationships of the model */
    function relationships(id: string): DtdlRelationship[] {
      return models.value.find((m) => m.id === id)?.relationships || []
    }

    /** Get all telemetries of the model */
    function telemetries(id: string): DtdlTelemetry[] {
      return models.value.find((m) => m.id === id)?.telemetries || []
    }

    /** Get all bases (ids) of the model */
    function bases(id: string): string[] {
      return models.value.find((m) => m.id === id)?.bases || []
    }

    /** Get an extended model by Id */
    function byId(id: string | undefined): DigitalTwinsModelDataExtended | undefined {
      return models.value.find((m) => m.id === id)
    }

    /** Get a list of extended models by base id(s) */
    function byBaseIds(
      baseIds: string | string[],
      includeExact?: boolean
    ): DigitalTwinsModelDataExtended[] {
      const _baseIds = Array.isArray(baseIds) ? baseIds : [baseIds]
      return models.value.filter(
        (m) =>
          (includeExact && _baseIds.includes(m.id)) || m.bases?.some((b) => _baseIds.includes(b))
      )
    }

    /** Returns a list of model ids by base id(s) */
    function idsByBaseIds(baseIds: string | string[], includeExact?: boolean): string[] {
      return byBaseIds(baseIds, includeExact).map((m) => m.id)
    }

    /** Returns a function that returns a property by model id and property name. Dotted properties are supported (maximum 1 dot). */
    function propertyByName(
      modelId: string,
      propertyName?: string
    ): DtdlProperty | DtdlMapSchema['mapValue'] | DtdlObjectSchema['fields'][0] | undefined {
      if (!propertyName) return undefined
      const modelProperties = properties(modelId)
      const propertyNameParts = propertyName?.split('.')
      const property = modelProperties.find((x) => x.name === propertyNameParts[0])
      if (!property) return
      if (propertyNameParts.length === 1) return property
      // for dotted properties, we try to retrieve the property of the child
      if ((property.schema as DtdlMapSchema)['@type'] === 'Map' && propertyNameParts.length === 2)
        return (property.schema as DtdlMapSchema).mapValue
      if (
        (property.schema as DtdlObjectSchema)['@type'] === 'Object' &&
        propertyNameParts.length === 2
      )
        return (property.schema as DtdlObjectSchema).fields.find(
          (f) => f.name === propertyNameParts[1]
        )
    }

    /** Returns a function that returns a property by model id and display name (reverse of propertyByName). */
    function propertyByDisplayName(
      modelId: string,
      displayName?: string,
      lang = 'en'
    ): DtdlProperty | undefined {
      if (!displayName) return undefined
      const modelProperties = properties(modelId)
      return modelProperties.find((x) => {
        const dn = getDisplayName(x, lang) || x.name
        return dn === displayName
      })
    }

    /** Get expression or formatted value for a twin expression */
    function expressionOrFormattedValue(
      twin: BasicDigitalTwin | Record<string, unknown>,
      expression: string
    ): string | undefined {
      return expression?.startsWith('=') || !(twin as BasicDigitalTwin).$metadata?.$model
        ? jexlEval<string>(expression, twin)
        : getFormattedValueByProperty(
            jexlEval<string>(expression, twin),
            propertyByName((twin as BasicDigitalTwin).$metadata.$model, expression)
          )
    }

    function relationshipByName(
      modelId: string,
      relationshipName: string
    ): DtdlRelationship | undefined {
      return relationships(modelId)?.find((r) => r.name === relationshipName)
    }

    /** Checks whether the provided twin is an instance of the provided base model(s) (returns type predicate) */
    function twinIsOfModel<T extends BasicDigitalTwin>(
      twin: BasicDigitalTwin,
      baseModelId: string[] | string | undefined
    ): twin is T {
      const _modelId = twin.$metadata.$model
      if (baseModelId && Array.isArray(baseModelId)) {
        return (
          baseModelId.includes(_modelId) ||
          baseModelId.some((mId) => bases(_modelId)?.includes(mId))
        )
      } else if (typeof baseModelId === 'string') {
        return _modelId === baseModelId || bases(_modelId)?.includes(baseModelId)
      } else return false
    }

    /** Checks whether the provided twin is a ViewerDataTwin */
    function isViewerDataTwin(twin: BasicDigitalTwin): twin is ViewerDataTwin {
      return twinIsOfModel(twin, ALLOWED_VIEWER_DATA_MODEL_IDS)
    }

    /** Checks whether the provided twin is a BlobTwin */
    function isBlobTwin(twin: BasicDigitalTwin): twin is BlobTwin {
      return twinIsOfModel(twin, ALLOWED_BLOB_MODEL_IDS)
    }

    /** Checks whether the provided twin is an AutodeskFileTwin */
    function isAutodeskFileTwin(twin: BasicDigitalTwin): twin is AutodeskFileTwin {
      return twinIsOfModel(twin, AUTODESK_FILE_MODEL_ID)
    }

    /** Checks whether the provided twin is an OneLakeFileTwin */
    function isOneLakeFileTwin(twin: BasicDigitalTwin): twin is OneLakeFileTwin {
      return twinIsOfModel(twin, ONELAKE_FILE_MODEL_ID)
    }

    /** Checks whether the provided twin is an EventTwin */
    function isEventTwin(twin: BasicDigitalTwin): twin is EventTwin {
      return twinIsOfModel(twin, ALLOWED_EVENT_MODEL_IDS)
    }

    /** Check if model is tagged as an abstract model (hidden) so you can't use it */
    function isHidden(modelId: string): boolean {
      const model = byId(modelId)
      if (!model) return true
      if (!model?.model?.comment) return false
      return model?.model?.comment?.toLowerCase().includes('hidden')
    }

    /** Gets the allowed relationship options (requires targets to specify version) */
    function relOptions(sourceModelId: string, targetModelId: string): DtdlRelationship[] {
      return (
        relationships(sourceModelId).filter(
          (x: { target: string }) =>
            !x.target || x.target === targetModelId || bases(targetModelId)?.includes(x.target)
        ) || []
      )
    }

    /** Gets the allowed incoming relationship options based on targetModelId (requires targets to specify version)  */
    function incomingRelOptions(targetModelId: string): DtdlRelationship[] {
      return (
        (
          [
            ...new Set(
              models.value
                .map(
                  (m: DigitalTwinsModelDataExtended) =>
                    m.relationships || ([] as DtdlRelationship[])
                )
                .flat()
            )
          ] as DtdlRelationship[]
        ).filter<DtdlRelationship>(
          (x): x is DtdlRelationship =>
            !x.target || x.target === targetModelId || bases(targetModelId)?.includes(x.target)
        ) || ([] as DtdlRelationship[])
      )
    }

    /** Gets the allowed relationship options names  (requires targets to specify version) */
    function relOptionsNames(sourceModelId: string, targetModelId: string): string[] {
      return [...new Set(relOptions(sourceModelId, targetModelId).map((x) => x.name))] as string[]
    }

    /** Gets the allowed incoming relationship options names based on targetModelId (requires targets to specify version) */
    function incomingRelOptionsNames(targetModelId: string): string[] {
      return [...new Set(incomingRelOptions(targetModelId).map((x) => x.name))] as string[]
    }

    /** Gets the displayName from a model Id */
    function modelDisplayName(modelId: string, lang?: string): string {
      const m = byId(modelId)
      return getDisplayName(m, lang) || modelId?.split(':').at(-1) || modelId
    }

    /** Gets the displayName from a model Id */
    function modelDescription(modelId: string, lang?: string): string | undefined {
      const m = byId(modelId)
      return getDescription(m, lang)
    }

    /** Gets the comment from a model Id */
    function modelComment(modelId: string): string | undefined {
      const m = byId(modelId)
      return m?.model?.comment
    }

    /** Gets the allowed child model ids by parent model ids (and optionally also child model ids) */
    function modelIdOptions(sourceModelIds: string[], targetModelIds?: string[]): string[] {
      // TODO simplify or split this function
      let modelIdOptions: string[] = []
      for (const sourceModelId of sourceModelIds) {
        const relOptions = relationships(sourceModelId)
        if (!relOptions || relOptions.length === 0) {
          // nothing allowed
          return []
        } else if (relOptions.some((x) => !x.target)) {
          // all allowed
          continue
        } else {
          // check what's allowed
          const currentTargetModelIdOptions: string[] = relOptions.reduce((arr, rel) => {
            if (!arr.includes(rel.target)) arr.push(rel.target)
            const allPossibleTargets = idsByBaseIds(rel.target)
            allPossibleTargets.forEach((target) => {
              if (!arr.includes(target)) arr.push(target)
            })
            return arr
          }, [] as string[])
          if (currentTargetModelIdOptions.length > 0 && !modelIdOptions) modelIdOptions = []
          currentTargetModelIdOptions.forEach((mId) => {
            if (!modelIdOptions.includes(mId)) modelIdOptions.push(mId)
          })
          modelIdOptions.forEach((mId, i) => {
            if (!currentTargetModelIdOptions.includes(mId)) modelIdOptions.splice(i, 1)
          })
        }
      }
      if (targetModelIds && targetModelIds.length > 0) {
        const targetModels = targetModelIds
          .map((mId) => byId(mId))
          .filter((m) => !!m) as DigitalTwinsModelDataExtended[]
        if (modelIdOptions.length > 0) {
          // if model options defined, we need to check which ones allow a relation with all targetModelIds
          modelIdOptions = modelIdOptions.filter((mId) =>
            relationships(mId)
              // check if any rel allows all or allows one of the bases to ALL targetmodels
              // eslint-disable-next-line sonarjs/no-nested-functions
              .some((x) => !x.target || targetModels.every((y) => y?.bases?.includes(x.target)))
          )
        } else if (modelIdOptions.length === 0) {
          // if no modelIdOptions (means everything is allowed) we just need to check which models allow to have a relationship with all targetModelIds
          modelIdOptions = modelIdOptions.filter((mId) =>
            relationships(mId).some(
              (x) =>
                !x.target ||
                // eslint-disable-next-line sonarjs/no-nested-functions
                targetModels.every((y) => x.target === y.id || y?.bases?.includes(x.target))
            )
          )
        }
      }
      return modelIdOptions
    }

    /** Gets the allowed target model ids based on the source model id and the relationship name */
    function targetModelIdOptionsByRelationship(
      sourceModelId: string,
      relationshipName?: string
    ): string[] {
      if (relationshipName) {
        const relationship = relationshipByName(sourceModelId, relationshipName)
        if (!relationship) return []
        if (!relationship.target) return models.value.map((m) => m.id)
        return [
          ...new Set(
            models.value
              .filter((m) => m.id === relationship.target || m.bases?.includes(relationship.target))
              .map((m) => m.id)
          )
        ]
      } else {
        const targetModelIds = relationships(sourceModelId).map((r) => r.target)
        if (targetModelIds.filter((id) => !id).length > 0) return models.value.map((m) => m.id)
        return [
          ...new Set(
            models.value
              .filter(
                (m) =>
                  targetModelIds.includes(m.id) || m.bases?.some((b) => targetModelIds.includes(b))
              )
              .map((m) => m.id)
          )
        ]
      }
    }

    /** Gets the allowed source model ids based on the target model id and the relationship name */
    function sourceModelIdOptionsByRelationship(
      targetModelId: string,
      relationshipName?: string
    ): string[] {
      return [
        ...new Set(
          models.value
            .filter(
              (m) =>
                (!relationshipName && !m.relationships) ||
                (m.relationships &&
                  m.relationships.filter(
                    (r) =>
                      (!r.target ||
                        r.target === targetModelId ||
                        bases(targetModelId)?.includes(r.target)) &&
                      (relationshipName ? r.name === relationshipName : true)
                  ).length > 0)
            )
            .map((m) => m.id)
        )
      ] as string[]
    }

    /** Nested model tree used in ModelTree component */
    const modelTree = computed(() => {
      const processModelTreeNode = (
        m: DigitalTwinsModelDataExtended
      ): DigitalTwinsModelDataExtended => {
        const hidden = m.model.comment?.toLowerCase().includes('hidden') || false
        return {
          ...m,
          hidden,
          // selectable: !m.decommissioned && !hidden,
          children: models.value
            .filter((p) => p.model.extends?.includes(m.id))
            .map(processModelTreeNode)
        }
      }
      return models.value
        .filter((m) => !m.model.extends || m.model.extends.length === 0)
        .map<DigitalTwinsModelDataExtended>(processModelTreeNode)
    })

    /**
     * Get dependent models (models that depend on the provided model ids)
     * @param modelIds - Model ids to check for dependent models
     * @returns - List of dependent model ids
     */
    function dependentModels(modelIds: string[]): string[] {
      return models.value
        .filter((m) =>
          modelIds.find(
            (x) =>
              m.bases?.includes(x) ||
              // eslint-disable-next-line sonarjs/no-nested-functions
              m.model.contents?.some((c) => c['@type'] === 'Component' && c.schema === x)
          )
        )
        .map((m) => m.id)
    }

    /**
     * Retrieve all models from server and store processed model list in store.
     * Resolves only once {@link models} has been populated, so callers can await it.
     * Concurrent calls share the same in-flight request.
     *
     * @param force - Start a new request even when one is already in flight. Use this
     *   after a mutation (add/delete/import): an in-flight request was issued *before*
     *   the mutation, so joining it would resolve with a pre-mutation model list and
     *   the UI would silently keep showing stale models.
     */
    async function getModels(force = false) {
      if (modelsLoad !== undefined && !force) return modelsLoad
      const generation = ++modelsLoadGeneration
      const load = (async () => {
        const fetched = await service.getModels()
        fetched.sort((a, b) => (a.id < b.id ? -1 : 1))
        // Yield to the event loop first so the (potentially heavy) processing
        // doesn't block the current task, but keep it inside the awaited promise
        // so awaiting getModels() guarantees the models are in the store.
        await new Promise((resolve) => {
          setTimeout(resolve, 0)
        })
        const processed = await processModels(fetched)
        // A newer request superseded this one while it was running — its response is
        // the more recent view of the server, so don't overwrite it.
        if (generation === modelsLoadGeneration) models.value = processed
      })()
      modelsLoad = load
      try {
        await load
      } finally {
        // Clear the in-flight marker so a later call can refresh the models.
        if (modelsLoad === load) modelsLoad = undefined
      }
    }

    /**
     * Resolves once models are present in the store: returns immediately when they
     * are already loaded, otherwise awaits the in-flight (or a new) request.
     *
     * Use this instead of reading `byId(...)` straight away in any code path that
     * classifies twins by their DTDL model (tile/feature-query filtering, model
     * inheritance checks). On a hard page load the models request is still in flight
     * while features come back instantly from localStorage, and an unguarded `byId`
     * silently returns undefined — which renders as an empty dashboard.
     *
     * Failures are swallowed (and logged): callers are render paths that must not
     * reject, and the next navigation retries.
     */
    async function ensureModelsLoaded() {
      if (models.value.length > 0) return
      try {
        await getModels()
      } catch (e) {
        console.warn(`Failed to load models for store '${storeId}'`, e)
      }
    }

    /**
     * Add new models and merge them into {@link models}.
     *
     * The merge is awaited rather than deferred to a `setTimeout`: callers that refresh
     * straight after (the admin models page, the model editor) would otherwise race the
     * deferred assignment and end up with either the pre-add or the pre-refresh list.
     */
    async function addModels(newModels: DtdlInterface | DtdlInterface[]) {
      if (!service.addModels) return
      const _models = Array.isArray(newModels) ? newModels : [newModels]
      const response = await service.addModels(_models)
      const created = createdModelData(response, _models)
      const createdIds = created.map((m) => m.id)
      models.value = await processModels([
        ...models.value.filter((m) => !createdIds.includes(m.id)),
        ...created
      ])
      return created
    }

    /** Delete a single model (will fail when dependent models are still present) */
    async function deleteModel(modelId: string) {
      if (!service.deleteModel) return
      await service.deleteModel(modelId)
      const idx = models.value.findIndex((m) => m.id === modelId)
      if (idx > -1) models.value.splice(idx, 1)
    }

    /**
     * Apply `action` to every given model, depth-first through its dependencies,
     * so dependents are always processed before the models they depend on. Shared
     * by the delete and decommission flows, which differ only in the leaf action.
     */
    async function forEachModelWithDependencies(
      modelIds: string[],
      action: (modelId: string) => Promise<void>
    ) {
      const processed = new Set<string>()
      // Models on the current path. Cyclic dependencies would otherwise recurse forever,
      // because a model only becomes `processed` after its action resolves.
      const inProgress = new Set<string>()
      const processRecursive = async (modelId: string): Promise<void> => {
        if (processed.has(modelId) || inProgress.has(modelId)) return
        inProgress.add(modelId)
        for (const childModelId of directChildModelIds(models.value, modelId)) {
          await processRecursive(childModelId)
        }
        inProgress.delete(modelId)
        try {
          await action(modelId)
          processed.add(modelId)
        } catch (e) {
          console.error(e)
        }
      }

      for (const modelId of modelIds) {
        await processRecursive(modelId)
      }
    }

    /** Delete multiple models and their dependencies */
    function deleteModelsRecursive(modelIds: string[]) {
      return forEachModelWithDependencies(modelIds, deleteModel)
    }

    /** Delete all models in the environment */
    async function deleteAllModels() {
      deleteModelsRecursive(models.value.map((m) => m.id))
    }

    /** Decommission a model */
    async function decommissionModel(modelId: string) {
      if (!service.decommissionModel) return
      await service.decommissionModel(modelId)
      const idx = models.value.findIndex((m) => m.id === modelId)
      if (idx > -1) models.value[idx].decommissioned = true
    }

    /** Decommission multiple models and their dependencies */
    function decommissionModelsRecursive(modelIds: string[]) {
      return forEachModelWithDependencies(modelIds, decommissionModel)
    }

    return {
      models,
      properties,
      components,
      relationships,
      telemetries,
      bases,
      byId,
      byBaseIds,
      idsByBaseIds,
      propertyByName,
      propertyByDisplayName,
      expressionOrFormattedValue,
      relationshipByName,
      twinIsOfModel,
      isViewerDataTwin,
      isBlobTwin,
      isAutodeskFileTwin,
      isOneLakeFileTwin,
      isEventTwin,
      isHidden,
      relOptions,
      incomingRelOptions,
      relOptionsNames,
      incomingRelOptionsNames,
      modelDisplayName,
      modelDescription,
      modelComment,
      modelIdOptions,
      targetModelIdOptionsByRelationship,
      sourceModelIdOptionsByRelationship,
      modelTree,
      dependentModels,
      getModels,
      ensureModelsLoaded,
      addModels,
      deleteModel,
      deleteModelsRecursive,
      deleteAllModels,
      decommissionModel,
      decommissionModelsRecursive
    }
  })
}

/**
 * Ids of models that directly depend on the given model — either by extending it
 * or by using it as a component schema. Shared by the recursive delete and
 * decommission flows so dependents are removed first.
 */
export const directChildModelIds = (
  modelList: DigitalTwinsModelDataExtended[],
  modelId: string
): string[] => {
  return modelList
    .filter(
      (m) =>
        m.model.extends?.includes(modelId) ||
        m.model.contents?.some((c) => c['@type'] === 'Component' && c.schema === modelId)
    )
    .map((m) => m.id)
}

/** Generate model list, including level */
export const processModels = async (models: DigitalTwinsModelData[]) => {
  const list: DigitalTwinsModelDataExtended[] = []
  const processModel = async (m: DigitalTwinsModelData, level: number) => {
    if (list.some((x) => x.id === m.id) || !m.model) return
    list.push({
      ...m,
      ...mapModel(models, m.model),
      model: m.model,
      level
    })
    const children = models.filter((p) => p.model?.extends?.includes(m.id))
    for (const childModel of children) {
      await new Promise<void>((resolve) => {
        setTimeout(async () => {
          await processModel(childModel, level + 1)
          resolve()
        }, 0)
      })
    }
  }
  for (const m of models.filter((m) => !m.model?.extends || m.model.extends.length === 0)) {
    await new Promise<void>((resolve) => {
      setTimeout(async () => {
        await processModel(m, 0)
        resolve()
      }, 0)
    })
  }
  return list
}

/** Helper function to map inherited model data */
export const mapModel = (
  models: DigitalTwinsModelData[],
  model: DtdlInterface
): {
  properties: DtdlProperty[]
  relationships: DtdlRelationship[]
  telemetries: DtdlTelemetry[]
  // TODO: add commands
  components: DtdlComponent[]
  bases: string[]
} => {
  // generate propertylist
  const contents: {
    properties: DtdlProperty[]
    relationships: DtdlRelationship[]
    telemetries: DtdlTelemetry[]
    // TODO: add commands
    components: DtdlComponent[]
    bases: string[]
  } = { properties: [], relationships: [], telemetries: [], components: [], bases: [] }

  // hold category orders
  // lowest order will be pickt up as the first category
  let categoryOrder: { [category: string]: number } = {}

  const resetCategoryOrder = () => {
    categoryOrder = {}
  }

  const compareCommentOrder = (a: DtdlProperty, b: DtdlProperty) => {
    const defaultOrder = 999
    // lowest order will be picked up as the first category and as first prop
    // if default order is a high number then it will be put as last category or prop
    const getOrder = (parsed: unknown, cat: unknown): number => {
      if (isNumeric(parsed)) return parsed
      else if (isNumeric(cat)) return cat
      else return defaultOrder
    }
    const defaultCategory = 'Properties'
    const aCategory = `${getCommentValue(a.comment, 'category')}` || defaultCategory
    const bCategory = `${getCommentValue(b.comment, 'category')}` || defaultCategory
    const aCommentOrder = getCommentValue(a.comment, 'order')
    const bCommentOrder = getCommentValue(b.comment, 'order')
    const aCommentOrderParsed = parseInt(`${aCommentOrder}`, 10)
    const bCommentOrderParsed = parseInt(`${bCommentOrder}`, 10)
    const aOrder = getOrder(aCommentOrderParsed, categoryOrder[aCategory])
    const bOrder = getOrder(bCommentOrderParsed, categoryOrder[bCategory])
    // if a has an order in the comments set the category order
    if (
      (!isNumeric(categoryOrder[aCategory]) && isNumeric(aCommentOrderParsed)) ||
      (isNumeric(categoryOrder[aCategory]) &&
        isNumeric(aCommentOrderParsed) &&
        aCommentOrderParsed < categoryOrder[aCategory])
    ) {
      categoryOrder[aCategory] = aCommentOrderParsed
    }
    // if b has an order in the comments set the category order
    if (
      (!isNumeric(categoryOrder[bCategory]) && isNumeric(bCommentOrderParsed)) ||
      (isNumeric(categoryOrder[bCategory]) &&
        isNumeric(bCommentOrderParsed) &&
        bCommentOrderParsed < categoryOrder[bCategory])
    ) {
      categoryOrder[bCategory] = bCommentOrderParsed
    }
    // aCategory is smaller then bCategory or there is no bCategory
    // aCategory equals bCategory and aCommentOrderParsed is smaller then bCommentOrderParsed or there is no bCommentOrderParsed
    // put a before b
    if (
      (isNumeric(categoryOrder[aCategory]) &&
        isNumeric(categoryOrder[bCategory]) &&
        categoryOrder[aCategory] < categoryOrder[bCategory]) ||
      (isNumeric(categoryOrder[aCategory]) && !isNumeric(categoryOrder[bCategory])) ||
      (isNumeric(categoryOrder[aCategory]) &&
        isNumeric(categoryOrder[bCategory]) &&
        categoryOrder[aCategory] === categoryOrder[bCategory] &&
        ((isNumeric(aCommentOrderParsed) && !isNumeric(bCommentOrderParsed)) ||
          (isNumeric(aCommentOrderParsed) &&
            isNumeric(bCommentOrderParsed) &&
            aCommentOrderParsed < bCommentOrderParsed)))
    ) {
      return -1
    }
    // aCategory is bigger then bCategory or there is no aCategory
    // aCategory equals bCategory and aCommentOrderParsed is bigger then bCommentOrderParsed or there is no aCommentOrderParsed
    // put a after b
    if (
      (isNumeric(categoryOrder[aCategory]) &&
        isNumeric(categoryOrder[bCategory]) &&
        categoryOrder[aCategory] > categoryOrder[bCategory]) ||
      (!isNumeric(categoryOrder[aCategory]) && isNumeric(categoryOrder[bCategory])) ||
      (isNumeric(categoryOrder[aCategory]) &&
        isNumeric(categoryOrder[bCategory]) &&
        categoryOrder[aCategory] === categoryOrder[bCategory] &&
        ((!isNumeric(aCommentOrderParsed) && isNumeric(bCommentOrderParsed)) ||
          (isNumeric(aCommentOrderParsed) &&
            isNumeric(bCommentOrderParsed) &&
            aCommentOrderParsed > bCommentOrderParsed)))
    ) {
      return 1
    }
    // sort without category order
    if (aOrder < bOrder) {
      // a is less than b by some ordering criterion
      return -1
    } else if (aOrder > bOrder) {
      // a is greater than b by the ordering criterion
      return 1
    }
    // a must be equal to b
    return 0
  }

  // recursively add all properties (also from parent models)
  const addModelContents = (model: DtdlInterface, reset = true) => {
    // reset category order
    if (reset) resetCategoryOrder()
    if (model.contents) {
      // workaround for model definition with just 1 content defined as object instead of array
      if (!Array.isArray(model.contents) && typeof model.contents === 'object')
        model.contents = [model.contents]
      contents.properties.unshift(
        ...model.contents
          .filter(
            (c): c is DtdlProperty =>
              (c['@type'] === 'Property' || c['@type'].includes('Property')) &&
              !contents.properties.some((p) => p.name === c.name)
          )
          .map<DtdlProperty>(
            (p) =>
              replaceUriKeys(p as unknown as Record<string, unknown>) as unknown as DtdlProperty
          )
      )
      contents.relationships.unshift(
        ...model.contents
          .filter(
            (c): c is DtdlRelationship =>
              (c['@type'] === 'Relationship' || c['@type'].includes('Relationship')) &&
              !contents.relationships.some((p) => p.name === c.name)
          )
          .map<DtdlRelationship>(
            (p) =>
              replaceUriKeys(p as unknown as Record<string, unknown>) as unknown as DtdlRelationship
          )
      )
      contents.telemetries.unshift(
        ...model.contents
          .filter(
            (c): c is DtdlTelemetry =>
              (c['@type'] === 'Telemetry' || c['@type'].includes('Telemetry')) &&
              !contents.telemetries.some((p) => p.name === c.name)
          )
          .map<DtdlTelemetry>(
            (p) =>
              replaceUriKeys(p as unknown as Record<string, unknown>) as unknown as DtdlTelemetry
          )
      )
      contents.components.unshift(
        ...model.contents
          .filter(
            (c): c is DtdlComponent =>
              (c['@type'] === 'Component' || c['@type'].includes('Component')) &&
              !contents.components.some((p) => p.name === c.name)
          )
          .map<DtdlComponent>(
            (p) =>
              replaceUriKeys(p as unknown as Record<string, unknown>) as unknown as DtdlComponent
          )
      )
    }
    if (model.extends) {
      if (Array.isArray(model.extends)) {
        model.extends.forEach((x) => {
          contents.bases.unshift(x)
          const baseModel = models.find((y) => y.id === x)?.model
          if (baseModel) addModelContents(baseModel, false)
        })
      } else {
        contents.bases.unshift(model.extends)
        const baseModel = models.find((y) => y.id === model.extends)?.model
        if (baseModel) addModelContents(baseModel, false)
      }
    }
  }

  addModelContents(model)

  // sort properties by commentOrder
  contents.properties.sort(compareCommentOrder)

  return contents
}

/** This is a helper to recursively clean DTDL models that were generated with OWL2DTDL as they have some JSON-LD compacting issues */
const replaceUriKeys = <T extends Record<string, unknown>>(originalObject: T): T => {
  const obj: Record<string, unknown> = { ...originalObject }
  for (const [k, v] of Object.entries(obj)) {
    const dtdlMapKv = k?.startsWith('dtmi') && dtdlKeyMap.find(([, dV]) => k?.startsWith(dV))
    let nK = k
    if (dtdlMapKv) {
      // Copy value reference to correct key
      obj[dtdlMapKv[0]] = obj[k]
      nK = dtdlMapKv[0]
      // delete non-compacted URI prop
      delete obj[k]
    }
    if (
      Array.isArray(v) &&
      v.length &&
      typeof v[0] === 'object' &&
      !Array.isArray(v[0]) &&
      v[0] !== null
    ) {
      for (let i = 0; i++; i < v.length) {
        ;(obj[nK] as Record<string, unknown>[])[i] = replaceUriKeys(
          (obj[nK] as Record<string, unknown>[])[i]
        )
      }
    } else if (!Array.isArray(v) && typeof v === 'object' && v !== null) {
      obj[nK] = replaceUriKeys(obj[nK] as Record<string, unknown>)
    }
  }
  return obj as T
}

/** Maps DTDL keys to their Uris, used to replace non-compacted URIs to proper keys */
const dtdlKeyMap = Object.entries({
  contents: 'dtmi:dtdl:property:contents',
  elementSchema: 'dtmi:dtdl:property:elementSchema',
  enumValue: 'dtmi:dtdl:property:enumValue',
  enumValues: 'dtmi:dtdl:property:enumValues',
  fields: 'dtmi:dtdl:property:fields',
  mapKey: 'dtmi:dtdl:property:mapKey',
  mapValue: 'dtmi:dtdl:property:mapValue',
  properties: 'dtmi:dtdl:property:properties',
  schema: 'dtmi:dtdl:property:schema',
  schemas: 'dtmi:dtdl:property:schemas',
  target: 'dtmi:dtdl:property:target',
  unit: 'dtmi:dtdl:property:unit',
  valueSchema: 'dtmi:dtdl:property:valueSchema'
})

export const useModelsStore = createModelStore('models', twinsApiService)

export const useAdminModelsStore = createModelStore('adminModels', envApiService)
